import JSZip from 'jszip'
import type { GeojsonCoordinate } from '/@/types/map'

/** DJI WPML：航点行（WGS84），index 为文件中的 wpml:index（通常从 0 递增） */
interface WpmlWaypointRow {
  folderOrder: number
  wpIndex: number
  coord: GeojsonCoordinate
  executeHeight?: number
}

export interface WaylinePreviewWaypoint {
  coord: GeojsonCoordinate
  /** 展示序号（默认 DJI 0-based index + 1） */
  index: number
  placemarkName?: string
  /** wpml:executeHeight（椭球/相对高由 executeHeightMode 决定，仅展示） */
  executeHeight?: number
}

export interface WaylinePreviewPolygon {
  ring: GeojsonCoordinate[]
  name?: string
}

export interface WaylinePreviewPoint {
  coord: GeojsonCoordinate
  name?: string
}

export interface WaylinePreviewModel {
  /** KML Document 名称或列表中的航线名 */
  routeName: string
  /** 主航线折线（WGS84），来自 WPML 航点顺序或 LineString */
  path: GeojsonCoordinate[]
  /** KMZ 内全部 LineString（通常比航点更密，为完整飞行轨迹） */
  linePaths: GeojsonCoordinate[][]
  waypoints: WaylinePreviewWaypoint[]
  polygons: WaylinePreviewPolygon[]
  takeoff?: WaylinePreviewPoint
  rth?: WaylinePreviewPoint
}

interface MutableModel {
  routeName: string
  /** 优先 waylines.wpml；若无则 template.kml 中带 index 的航点 */
  wpmlRows: WpmlWaypointRow[]
  lineStrings: GeojsonCoordinate[][]
  polygons: WaylinePreviewPolygon[]
  takeoff?: WaylinePreviewPoint
  rth?: WaylinePreviewPoint
  genericPoints: WaylinePreviewPoint[]
}

function textContent (el: Element | null | undefined): string {
  return el?.textContent?.trim() ?? ''
}

function appendCoordinateTuples (raw: string, out: GeojsonCoordinate[]) {
  const parts = raw.trim().split(/[\s\n\r\t]+/).filter(Boolean)
  for (const p of parts) {
    const seg = p.split(',')
    if (seg.length < 2) continue
    const lng = parseFloat(seg[0])
    const lat = parseFloat(seg[1])
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      out.push([lng, lat])
    }
  }
}

function parseCoordinatesElement (node: Element | null): GeojsonCoordinate[] {
  if (!node) return []
  const raw = node.textContent?.trim() ?? ''
  const pts: GeojsonCoordinate[] = []
  appendCoordinateTuples(raw, pts)
  return pts
}

function isTakeoffName (name: string): boolean {
  const n = name.trim()
  if (!n) return false
  return /起飞|起飛|take[\s_-]?off|^to$/i.test(n)
}

function isRthName (name: string): boolean {
  const n = name.trim()
  if (!n) return false
  return /返航|返航点|rth|rtl|return|landing|备降|降落点/i.test(n)
}

function getFirstChildByLocalName (parent: Element, local: string): Element | null {
  for (let i = 0; i < parent.children.length; i++) {
    const c = parent.children[i]
    if (c.localName === local) return c
  }
  return null
}

function descendantsByLocalName (root: Element, local: string): Element[] {
  return Array.from(root.getElementsByTagName('*')).filter(el => el.localName === local)
}

function getPlacemarkName (pm: Element): string {
  return textContent(getFirstChildByLocalName(pm, 'name'))
}

function getDocumentElement (doc: Document): Element | null {
  const all = Array.from(doc.getElementsByTagName('*'))
  return all.find(e => e.localName === 'Document') || null
}

function findOuterRingCoords (poly: Element): GeojsonCoordinate[] {
  const outers = descendantsByLocalName(poly, 'outerBoundaryIs')
  if (!outers.length) return []
  const rings = descendantsByLocalName(outers[0], 'LinearRing')
  if (!rings.length) return []
  const coordEl = descendantsByLocalName(rings[0], 'coordinates')[0]
  return parseCoordinatesElement(coordEl ?? null)
}

function findPointCoordInPlacemark (pm: Element): GeojsonCoordinate | null {
  const points = descendantsByLocalName(pm, 'Point')
  for (const pt of points) {
    const coordEl = descendantsByLocalName(pt, 'coordinates')[0]
    const arr = parseCoordinatesElement(coordEl ?? null)
    if (arr.length) return arr[0]
  }
  return null
}

function documentRouteName (doc: Document): string {
  const docEl = getDocumentElement(doc)
  if (!docEl) return ''
  const n = getFirstChildByLocalName(docEl, 'name')
  return textContent(n)
}

/**
 * DJI waylines.wpml / template.kml：带 wpml:index 的 Placemark + Point 视为航点。
 * @see https://github.com/dji-sdk/Cloud-API-Doc/blob/master/docs/en/60.api-reference/00.dji-wpml/30.waylines-wpml.md
 */
function extractWpmlWaypointRows (doc: Document): WpmlWaypointRow[] {
  const docEl = getDocumentElement(doc)
  if (!docEl) return []

  const rows: WpmlWaypointRow[] = []

  const processPlacemark = (pm: Element, folderOrder: number) => {
    if (descendantsByLocalName(pm, 'Polygon').length) return
    if (descendantsByLocalName(pm, 'LineString').length) return
    const pt = findPointCoordInPlacemark(pm)
    if (!pt) return
    const idxCandidates = descendantsByLocalName(pm, 'index').filter(el => pm.contains(el))
    const idxEl = idxCandidates[0]
    if (!idxEl) return
    const wpIndex = parseInt(textContent(idxEl), 10)
    if (!Number.isFinite(wpIndex) || wpIndex < 0) return
    let executeHeight: number | undefined
    const hEl = descendantsByLocalName(pm, 'executeHeight').find(el => pm.contains(el))
    if (hEl) {
      const hv = parseFloat(textContent(hEl))
      if (Number.isFinite(hv)) executeHeight = hv
    }
    rows.push({ folderOrder, wpIndex, coord: pt, executeHeight })
  }

  const folders = Array.from(docEl.children).filter(c => c.localName === 'Folder')
  if (folders.length) {
    folders.forEach((folder, folderOrder) => {
      Array.from(folder.children).forEach(child => {
        if (child.localName === 'Placemark') processPlacemark(child as Element, folderOrder)
      })
    })
  } else {
    Array.from(docEl.children).forEach(child => {
      if (child.localName === 'Placemark') processPlacemark(child as Element, 0)
    })
  }

  return rows
}

function mergeLineStringsFromDoc (doc: Document, model: MutableModel) {
  const placemarks = Array.from(doc.getElementsByTagName('*')).filter(e => e.localName === 'Placemark')
  for (const pm of placemarks) {
    for (const line of descendantsByLocalName(pm, 'LineString')) {
      const coordEl = descendantsByLocalName(line, 'coordinates')[0]
      const pts = parseCoordinatesElement(coordEl ?? null)
      if (pts.length >= 2) {
        model.lineStrings.push(pts)
      }
    }
  }
}

function mergePolygonsFromDoc (doc: Document, model: MutableModel) {
  const placemarks = Array.from(doc.getElementsByTagName('*')).filter(e => e.localName === 'Placemark')
  for (const pm of placemarks) {
    const pmName = getPlacemarkName(pm)
    for (const poly of descendantsByLocalName(pm, 'Polygon')) {
      const ring = findOuterRingCoords(poly)
      if (ring.length >= 3) {
        model.polygons.push({ ring, name: pmName || undefined })
      }
    }
  }
}

/** template.kml：任务区、起飞/返航命名点、非 WPML 点；可选模板内航点（无 waylines 时） */
function mergeTemplateKml (text: string, model: MutableModel, isPrimaryWpmlSource: boolean) {
  const doc = new DOMParser().parseFromString(text, 'text/xml')
  if (doc.getElementsByTagName('parsererror').length) return

  const dn = documentRouteName(doc)
  if (dn && !model.routeName) model.routeName = dn

  mergePolygonsFromDoc(doc, model)
  mergeLineStringsFromDoc(doc, model)

  const placemarks = Array.from(doc.getElementsByTagName('*')).filter(e => e.localName === 'Placemark')
  for (const pm of placemarks) {
    const pmName = getPlacemarkName(pm)
    const hasPolygon = descendantsByLocalName(pm, 'Polygon').length > 0
    const hasLine = descendantsByLocalName(pm, 'LineString').length > 0
    const hasWpmlIndex = descendantsByLocalName(pm, 'index').some(el => pm.contains(el))
    const ptCoord = findPointCoordInPlacemark(pm)
    if (!ptCoord || hasPolygon || hasLine) continue

    if (hasWpmlIndex) {
      continue
    }

    if (isTakeoffName(pmName)) {
      model.takeoff = { coord: ptCoord, name: pmName }
    } else if (isRthName(pmName)) {
      model.rth = { coord: ptCoord, name: pmName }
    } else {
      model.genericPoints.push({ coord: ptCoord, name: pmName || undefined })
    }
  }

  if (isPrimaryWpmlSource) {
    const rows = extractWpmlWaypointRows(doc)
    if (rows.length) model.wpmlRows = rows
  }
}

/** waylines.wpml：可执行航线，航点以 wpml:index 为准 */
function mergeWaylinesWpml (text: string, model: MutableModel) {
  const doc = new DOMParser().parseFromString(text, 'text/xml')
  if (doc.getElementsByTagName('parsererror').length) return

  const dn = documentRouteName(doc)
  if (dn) model.routeName = dn

  const rows = extractWpmlWaypointRows(doc)
  if (rows.length) {
    model.wpmlRows = rows
  }

  mergeLineStringsFromDoc(doc, model)
  mergePolygonsFromDoc(doc, model)
}

function mergeGenericKml (text: string, model: MutableModel) {
  const doc = new DOMParser().parseFromString(text, 'text/xml')
  if (doc.getElementsByTagName('parsererror').length) return
  if (!model.routeName) {
    const dn = documentRouteName(doc)
    if (dn) model.routeName = dn
  }
  mergePolygonsFromDoc(doc, model)
  mergeLineStringsFromDoc(doc, model)

  const placemarks = Array.from(doc.getElementsByTagName('*')).filter(e => e.localName === 'Placemark')
  for (const pm of placemarks) {
    const pmName = getPlacemarkName(pm)
    const hasPolygon = descendantsByLocalName(pm, 'Polygon').length > 0
    const hasLine = descendantsByLocalName(pm, 'LineString').length > 0
    const hasWpmlIndex = descendantsByLocalName(pm, 'index').some(el => pm.contains(el))
    const ptCoord = findPointCoordInPlacemark(pm)
    if (!ptCoord || hasPolygon || hasLine) continue
    if (hasWpmlIndex) {
      if (!model.wpmlRows.length) {
        model.wpmlRows = extractWpmlWaypointRows(doc)
      }
      continue
    }
    if (isTakeoffName(pmName)) {
      model.takeoff = { coord: ptCoord, name: pmName }
    } else if (isRthName(pmName)) {
      model.rth = { coord: ptCoord, name: pmName }
    } else {
      model.genericPoints.push({ coord: ptCoord, name: pmName || undefined })
    }
  }
}

function longestLineString (lines: GeojsonCoordinate[][]): GeojsonCoordinate[] {
  if (!lines.length) return []
  return lines.reduce((a, b) => (b.length > a.length ? b : a), lines[0])
}

function dedupeConsecutive (pts: GeojsonCoordinate[]): GeojsonCoordinate[] {
  if (pts.length <= 1) return pts
  const res: GeojsonCoordinate[] = [pts[0]]
  for (let i = 1; i < pts.length; i++) {
    const a = res[res.length - 1]
    const b = pts[i]
    if (a[0] !== b[0] || a[1] !== b[1]) {
      res.push(b)
    }
  }
  return res
}

function coordKey (c: GeojsonCoordinate): string {
  return `${c[0].toFixed(6)},${c[1].toFixed(6)}`
}

function buildLinePaths (m: MutableModel): GeojsonCoordinate[][] {
  return m.lineStrings
    .map(line => dedupeConsecutive(line))
    .filter(line => line.length >= 2)
}

function finalizeModel (m: MutableModel): WaylinePreviewModel {
  const linePaths = buildLinePaths(m)
  if (m.wpmlRows.length >= 1) {
    const sorted = [...m.wpmlRows].sort((a, b) =>
      a.folderOrder !== b.folderOrder ? a.folderOrder - b.folderOrder : a.wpIndex - b.wpIndex
    )
    const pathPts = dedupeConsecutive(sorted.map(r => r.coord))
    let path = pathPts.length >= 2 ? pathPts : []
    const linePath = dedupeConsecutive(longestLineString(m.lineStrings))
    if (linePath.length >= 2) {
      path = linePath
    } else if (path.length < 2 && sorted.length >= 2) {
      path = dedupeConsecutive(sorted.map(r => r.coord))
    }

    const waypoints: WaylinePreviewWaypoint[] = sorted.map(r => ({
      index: r.wpIndex + 1,
      coord: r.coord,
      executeHeight: r.executeHeight
    }))

    return {
      routeName: m.routeName,
      path,
      linePaths,
      waypoints,
      polygons: m.polygons,
      takeoff: m.takeoff,
      rth: m.rth
    }
  }

  let path = dedupeConsecutive(longestLineString(m.lineStrings))
  const takeoff = m.takeoff
  const rth = m.rth

  const wpMap = new Map<string, WaylinePreviewPoint>()
  for (const p of m.genericPoints) {
    wpMap.set(coordKey(p.coord), p)
  }
  const genericDeduped = Array.from(wpMap.values())

  if (path.length < 2 && genericDeduped.length >= 2) {
    path = genericDeduped.map(p => p.coord)
  }

  const used = new Set<string>()
  if (takeoff) used.add(coordKey(takeoff.coord))
  if (rth) used.add(coordKey(rth.coord))

  const waypoints: WaylinePreviewWaypoint[] = []
  let idx = 0
  for (const p of genericDeduped) {
    const k = coordKey(p.coord)
    if (used.has(k)) continue
    used.add(k)
    idx++
    waypoints.push({
      index: idx,
      coord: p.coord,
      placemarkName: p.name
    })
  }

  return {
    routeName: m.routeName,
    path,
    linePaths,
    waypoints,
    polygons: m.polygons,
    takeoff,
    rth
  }
}

function normalizeZipPath (p: string): string {
  return p.replace(/\\/g, '/').toLowerCase()
}

/**
 * 按 DJI WPML KMZ 解析：`wpmz/waylines.wpml` 航点（wpml:index + Point）、
 * `wpmz/template.kml` 任务区多边形与起飞/返航等命名点；坐标为 WGS84。
 */
export async function parseWaylineKmzModel (blob: Blob): Promise<WaylinePreviewModel> {
  const buf = await blob.arrayBuffer()
  const zip = await JSZip.loadAsync(buf)
  const names = Object.keys(zip.files).filter(n => !zip.files[n].dir)
  const kmlLike = names.filter(n => /^wpmz\/.+\.(kml|wpml)$/i.test(n.replace(/\\/g, '/')))
  const paths = kmlLike.length > 0 ? kmlLike : names.filter(n => /\.(kml|wpml)$/i.test(n))

  const model: MutableModel = {
    routeName: '',
    wpmlRows: [],
    lineStrings: [],
    polygons: [],
    genericPoints: []
  }

  const byNorm = new Map<string, string>()
  for (const p of paths) {
    byNorm.set(normalizeZipPath(p), p)
  }

  const waylinesPath = [...byNorm.keys()].find(k => k.endsWith('waylines.wpml'))
  const templatePath = [...byNorm.keys()].find(k => k.endsWith('template.kml'))

  if (templatePath) {
    const raw = await zip.file(byNorm.get(templatePath)!)!.async('text')
    mergeTemplateKml(raw, model, !waylinesPath)
  }

  if (waylinesPath) {
    const raw = await zip.file(byNorm.get(waylinesPath)!)!.async('text')
    mergeWaylinesWpml(raw, model)
  }

  for (const p of paths) {
    const n = normalizeZipPath(p)
    if (n.endsWith('waylines.wpml') || n.endsWith('template.kml')) continue
    const raw = await zip.file(p)!.async('text')
    mergeGenericKml(raw, model)
  }

  return finalizeModel(model)
}

/** @deprecated 仅折线路径；请优先使用 parseWaylineKmzModel */
export async function parseWaylineKmzBlob (blob: Blob): Promise<GeojsonCoordinate[]> {
  const m = await parseWaylineKmzModel(blob)
  if (m.path.length >= 2) return m.path
  if (m.waypoints.length >= 2) {
    return m.waypoints.map(w => w.coord)
  }
  if (m.takeoff && m.rth) {
    return [m.takeoff.coord, m.rth.coord]
  }
  if (m.takeoff) return [m.takeoff.coord]
  if (m.rth) return [m.rth.coord]
  for (const poly of m.polygons) {
    if (poly.ring.length) return poly.ring
  }
  return []
}
